import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/common';
import { CuttingConfigForm } from '../CuttingConfigForm';
import { useUIStore } from '@/store/uiStore';
import { toast } from '@/store/toastStore';
import { cuttingConfigApi } from '@/api/endpoints/cuttingConfigs';
import {
  CuttingConfigLine,
  CreateCuttingConfigRequest,
  UpdateCuttingConfigRequest,
} from '@/types/cutting';

interface CreateCuttingConfigModalProps {
  onSuccess?: () => void;
}

export const CreateCuttingConfigModal: React.FC<CreateCuttingConfigModalProps> = ({
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { modals, closeModal, refreshCallbacks } = useUIStore();
  const [loading, setLoading] = useState(false);

  const isOpen = modals.createCuttingConfig;

  const handleSubmit = async (data: CreateCuttingConfigRequest | UpdateCuttingConfigRequest, configLines?: CuttingConfigLine[]) => {
    setLoading(true);
    try {
      // Step 1: Create the cutting config
      const createdConfig = await cuttingConfigApi.createCuttingConfig(data as CreateCuttingConfigRequest);

      // Step 2: Create config lines if any exist
      if (configLines && configLines.length > 0 && createdConfig.code) {
        const configLineData = configLines.map(line => ({
          cuttingSizeId: line.cuttingSizeId,
          quantity: line.quantity,
          lossRate: line.lossRate,
          note: line.note,
        }));

        await cuttingConfigApi.createBulkCuttingConfigLines(createdConfig.code, configLineData);
      }

      toast.success('Success', t('cuttingConfigs.messages.createSuccess'));

      // Trigger refresh in parent component
      refreshCallbacks.cuttingConfigs?.();

      closeModal('createCuttingConfig');
      onSuccess?.();
    } catch (error: any) {
      console.error('Error creating cutting config:', error);
      const errorMessage = error.message || t('cuttingConfigs.messages.createError');
      toast.error('Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndClose = async (data: CreateCuttingConfigRequest | UpdateCuttingConfigRequest, configLines?: CuttingConfigLine[]) => {
    await handleSubmit(data, configLines);
  };

  const handleCancel = () => {
    closeModal('createCuttingConfig');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={t('cuttingConfigs.actions.create')}
      size="xl"
    >
      <CuttingConfigForm
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onSaveAndClose={handleSaveAndClose}
        loading={loading}
      />
    </Modal>
  );
};
